import { Alert, AlertDescription, AlertTitle, Button, Spinner } from '@1trading/react';
import { Bot, RefreshCw, TriangleAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import { PageHeader } from '@/components/layout/page-header';
import { AppInfoBar } from '@/features/market-maker/app-info-bar';
import { BalancesSection } from '@/features/market-maker/balances-section';
import { MarketsSection } from '@/features/market-maker/markets-section';
import { SimulationsSection } from '@/features/market-maker/simulations-section';
import { useMarketMakerInfo, useMarketMakerMarkets, useMarketMakerSimulations } from '@/hooks/queries/market-maker/use-market-maker';
import { config } from '@/lib/env';
import type { MarketStatus, SimulationsResponse } from '@/services/api/market-maker';

// Stable references — inline fallbacks would recreate per render
const EMPTY_MARKETS: MarketStatus[] = [];
const EMPTY_SIMULATIONS: SimulationsResponse = {};

// All hooks live below the env gate (in MarketMakerConsole) so the gated render
// mounts NO queries — a request must never be constructed against an unset base URL.
const MarketMakerConsole = () => {
  const marketsQuery = useMarketMakerMarkets();
  const infoQuery = useMarketMakerInfo();
  const simulationsQuery = useMarketMakerSimulations();
  // The header spinner tracks the MANUAL refresh only — binding it to
  // isFetching would pulse on every 10 s poll tick.
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);

  const markets = marketsQuery.data?.markets ?? EMPTY_MARKETS;
  const marketIds = useMemo(() => markets.map((market) => market.market), [markets]);

  // Balances are deliberately excluded — that endpoint is expensive and manual-only
  const handleRefresh = async () => {
    setIsManualRefreshing(true);
    try {
      await Promise.all([marketsQuery.refetch(), infoQuery.refetch(), simulationsQuery.refetch()]);
    } finally {
      setIsManualRefreshing(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6">
      <PageHeader
        actions={
          <Button aria-label="Refresh" onClick={() => void handleRefresh()} size="icon" type="button" variant="ghost">
            {isManualRefreshing ? <Spinner className="size-4" /> : <RefreshCw className="size-4" />}
          </Button>
        }
        title="Market Maker"
      />

      {marketsQuery.isError ? (
        // Deliberate deviation: legacy showed NOTHING when the service was
        // down; a quiet inline banner replaces that. Poll failures still never toast.
        <div className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm text-amber-500">
          <TriangleAlert className="size-4 shrink-0" />
          Market maker service unreachable — showing last known data.
        </div>
      ) : null}

      {infoQuery.data ? <AppInfoBar info={infoQuery.data} /> : null}

      <MarketsSection isPending={marketsQuery.isPending} markets={markets} />

      <SimulationsSection markets={marketIds} simulations={simulationsQuery.data ?? EMPTY_SIMULATIONS} />

      <BalancesSection />
    </div>
  );
};
MarketMakerConsole.displayName = 'MarketMakerConsole';

const MarketMakerPage = () => {
  // Route self-gating: routing.tsx registers routes for
  // nav-disabled items, so the URL resolves even when the flag is off.
  if (!config.MARKET_MAKER_ENABLED) {
    return (
      <div className="mx-auto w-full max-w-xl pt-16">
        <Alert>
          <Bot className="size-4" />
          <AlertTitle>Market Maker</AlertTitle>
          <AlertDescription>The market maker console is not available in this environment.</AlertDescription>
        </Alert>
      </div>
    );
  }
  return <MarketMakerConsole />;
};
MarketMakerPage.displayName = 'MarketMakerPage';

export default MarketMakerPage;
